import React, { useState, useEffect, useMemo } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Box,
  Chip,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Divider,
  Paper,
  Stack,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { format, isValid } from "date-fns";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DialogLoading from "../App/DialogLoading";
import BaseDialog from "../utils/dialog/BaseDialog";
import useRolloverPreview from "./hooks/useRolloverPreview";
import RolloverEmptyState from "./RolloverEmptyState";
import RolloverYearSummary from "./RolloverYearSummary";

const EMPTY_COMPUTATIONS = [];
const formatMonth = (value) => {
  const date = value ? new Date(value) : null;
  return date && isValid(date) ? format(date, "MMM yyyy") : "Date unavailable";
};
const getPeriodLabel = (computation) =>
  computation.isFullYear
    ? `Full year ending ${formatMonth(computation.financialYearEnd)}`
    : `${formatMonth(computation.financialYearStart)} – ${formatMonth(computation.financialYearEnd)}`;

const PreviewItems = ({ items }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
      gap: 2,
    }}
  >
    {items.map((item) => (
      <Stack key={item.id} direction='row' spacing={1} alignItems='flex-start'>
        <CheckCircleOutlineIcon
          sx={{ fontSize: 18, color: "primary.main", mt: 0.25 }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant='body2'
            fontWeight={600}
            sx={{ overflowWrap: "anywhere" }}
          >
            {item.label}
          </Typography>
          {item.reference && (
            <Typography variant='caption' color='text.secondary'>
              {item.reference}
            </Typography>
          )}
          <Typography
            variant='caption'
            color='text.secondary'
            display='block'
            sx={{ lineHeight: 1.6 }}
          >
            {item.description}
          </Typography>
        </Box>
      </Stack>
    ))}
  </Box>
);

const RolloverDataDialog = ({
  loadingComputations,
  computationsError,
  onRetryComputations,
  open,
  onClose,
  onConfirm,
  previousYearComputations = EMPTY_COMPUTATIONS,
  currentYearOfAssessment,
  currentTaxComputationId,
  initialSourceId,
}) => {
  const [step, setStep] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const computations = useMemo(
    () =>
      previousYearComputations
        .filter(
          (comp) =>
            Number(comp.yearOfAssessment) < Number(currentYearOfAssessment)
        )
        .sort(
          (left, right) =>
            Number(right.yearOfAssessment) - Number(left.yearOfAssessment) ||
            (Date.parse(right.financialYearEnd) || 0) -
              (Date.parse(left.financialYearEnd) || 0)
        ),
    [previousYearComputations, currentYearOfAssessment]
  );
  const selectedComputation =
    computations.find((comp) => comp.tc_id === selectedId) ||
    computations[0] ||
    null;
  const preview = useRolloverPreview({
    open,
    enabled: !loadingComputations && !computationsError,
    targetId: currentTaxComputationId,
    sourceId: selectedComputation?.tc_id,
  });

  useEffect(() => {
    setStep(0);
    setSelectedId(open ? initialSourceId || null : null);
  }, [open, initialSourceId]);

  const canContinue =
    !loadingComputations && !computationsError && Boolean(selectedComputation);
  const canConfirm =
    canContinue &&
    !preview.loading &&
    !preview.error &&
    preview.data?.totalItems > 0;
  const handleConfirm = () => {
    if (!canConfirm || step !== 1) return;
    onConfirm({
      sourceComputation: selectedComputation,
      rolloverType: "partial",
    });
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      title='Roll Over Previous Year Data'
      subtitle={`Choose a previous tax computation and review what carries forward to YA ${currentYearOfAssessment}.`}
      icon={<RefreshIcon />}
      paperSx={{ maxWidth: 780 }}
      contentSx={{ px: { xs: 2, sm: 3 } }}
      actions={
        <>
          <Button
            onClick={onClose}
            color='inherit'
            sx={{ mr: "auto", color: "text.secondary", textTransform: "none" }}
          >
            Cancel
          </Button>
          {step === 1 && (
            <Button onClick={() => setStep(0)} sx={{ textTransform: "none" }}>
              Back
            </Button>
          )}
          <Button
            variant='contained'
            disabled={step === 0 ? !canContinue : !canConfirm}
            onClick={step === 0 ? () => setStep(1) : handleConfirm}
            endIcon={step === 0 ? <ArrowForwardIcon /> : undefined}
            sx={{ textTransform: "none" }}
          >
            {step === 0 ? "Review data" : "Continue to confirmation"}
          </Button>
        </>
      }
    >
      {step === 0 ? (
        <Stack spacing={2} aria-busy={Boolean(loadingComputations)}>
          <Box>
            <Typography variant='subtitle1' fontWeight={600}>
              Select a previous tax computation
            </Typography>
          </Box>
          {loadingComputations ? (
            <DialogLoading
              title='Loading previous year computations...'
              content='Finding available computations for this company.'
            />
          ) : computationsError ? (
            <Alert
              severity='error'
              action={
                onRetryComputations && (
                  <Button
                    color='inherit'
                    size='small'
                    onClick={onRetryComputations}
                  >
                    Retry
                  </Button>
                )
              }
            >
              {computationsError}
            </Alert>
          ) : computations.length === 0 ? (
            <RolloverEmptyState
              currentYearOfAssessment={currentYearOfAssessment}
            />
          ) : (
            <Paper
              variant='outlined'
              sx={{ maxHeight: 340, overflow: "auto", borderRadius: 2 }}
            >
              <List disablePadding aria-label='Previous year tax computations'>
                {computations.map((computation, index) => (
                  <React.Fragment key={computation.tc_id}>
                    {index > 0 && <Divider component='li' />}
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={
                          selectedComputation?.tc_id === computation.tc_id
                        }
                        onClick={() => setSelectedId(computation.tc_id)}
                        sx={{ px: 2, py: 1.5 }}
                      >
                        <Radio
                          checked={
                            selectedComputation?.tc_id === computation.tc_id
                          }
                          size='small'
                          tabIndex={-1}
                          inputProps={{
                            "aria-label": `Select YA ${computation.yearOfAssessment}`,
                            readOnly: true,
                          }}
                          sx={{ mr: 1 }}
                        />
                        <ListItemText
                          disableTypography
                          primary={
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                              sx={{ mb: 0.5, flexWrap: "wrap" }}
                            >
                              <Typography variant='body1' fontWeight={600}>
                                YA {computation.yearOfAssessment}
                              </Typography>
                            </Stack>
                          }
                          secondary={
                            <Stack spacing={0.5}>
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={0.75}
                              >
                                <CalendarTodayIcon
                                  sx={{ fontSize: 14, color: "text.secondary" }}
                                />
                                <Typography
                                  variant='caption'
                                  color='text.secondary'
                                >
                                  {getPeriodLabel(computation)}
                                </Typography>
                              </Stack>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                              >
                                {computation.currency}
                                {computation.currency &&
                                  computation.currency !== "SGD" &&
                                  ` · Rate: ${computation.currency_rate}`}
                              </Typography>
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  </React.Fragment>
                ))}
              </List>
            </Paper>
          )}
          {canContinue && (
            <Box role='status' aria-live='polite'>
              {preview.loading ? (
                <Typography variant='caption' color='text.secondary'>
                  Checking eligible balances for YA{" "}
                  {selectedComputation.yearOfAssessment}…
                </Typography>
              ) : preview.error ? (
                <Alert
                  severity='error'
                  action={
                    <Button
                      color='inherit'
                      size='small'
                      onClick={preview.retry}
                    >
                      Retry preview
                    </Button>
                  }
                >
                  {preview.error}
                </Alert>
              ) : preview.data ? (
                <Typography variant='caption' color='text.secondary'>
                  {preview.data.totalItems > 0
                    ? `${preview.data.totalItems} items available to roll over. Review them before confirming.`
                    : "No eligible balances or schedules in this computation. Choose another year."}
                </Typography>
              ) : null}
            </Box>
          )}
        </Stack>
      ) : (
        <Stack spacing={2.5}>
          {selectedComputation && (
            <RolloverYearSummary
              sourceYear={selectedComputation.yearOfAssessment}
              targetYear={currentYearOfAssessment}
              sourcePeriod={getPeriodLabel(selectedComputation)}
            />
          )}
          <Box>
            <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
              Choose rollover type
            </Typography>
            <RadioGroup
              value='partial'
              aria-label='Rollover type'
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              <Paper
                variant='outlined'
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderWidth: 2,
                  borderColor: "primary.main",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  boxShadow: (theme) =>
                    `0 0 0 3px ${alpha(theme.palette.primary.main, 0.06)}`,
                }}
              >
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                  useFlexGap
                  flexWrap='wrap'
                  gap={1}
                >
                  <FormControlLabel
                    value='partial'
                    control={<Radio size='small' sx={{ p: 0.5, mr: 0.75 }} />}
                    label={
                      <Typography
                        variant='body2'
                        fontWeight={700}
                        color='primary.dark'
                      >
                        Partial Rollover
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Chip
                    label='Selected'
                    icon={<CheckCircleOutlineIcon />}
                    size='small'
                    sx={{
                      height: 24,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: 600,
                      "& .MuiChip-icon": { color: "inherit", fontSize: 16 },
                    }}
                  />
                </Stack>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  display='block'
                  sx={{ mt: 0.5 }}
                >
                  Carry forward eligible balances and schedules into the new
                  year.
                </Typography>
              </Paper>
              <Paper
                variant='outlined'
                sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}
              >
                <FormControlLabel
                  value='full'
                  disabled
                  control={<Radio size='small' />}
                  label={
                    <Typography variant='body2' fontWeight={600}>
                      Full Rollover
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
                <Chip
                  label='Coming soon'
                  size='small'
                  sx={{ ml: 0.5, height: 22 }}
                />
                <Typography
                  variant='caption'
                  color='text.secondary'
                  display='block'
                  sx={{ mt: 0.5 }}
                >
                  Copy the computation structure, descriptions and references
                  with numerical fields cleared.
                </Typography>
              </Paper>
            </RadioGroup>
          </Box>
          <Box aria-live='polite' aria-busy={Boolean(preview.loading)}>
            <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 0.5 }}>
              What will carry forward
            </Typography>
            {preview.loading ? (
              <DialogLoading
                title='Preparing rollover preview...'
                content='Checking schedules in the selected tax computation'
              />
            ) : preview.error ? (
              <Alert
                severity='error'
                action={
                  <Button color='inherit' size='small' onClick={preview.retry}>
                    Retry
                  </Button>
                }
              >
                {preview.error}
              </Alert>
            ) : (
              preview.data && (
                <Stack spacing={2}>
                  <Typography variant='caption' color='text.secondary'>
                    {preview.data.note}
                  </Typography>
                  {preview.data.totalItems === 0 && (
                    <Alert severity='info'>
                      No eligible balances or schedules are available to roll
                      over from this computation.
                    </Alert>
                  )}
                  {preview.data.groups.map((group) => (
                    <Accordion
                      key={group.id}
                      defaultExpanded={group.items.length <= 4}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack
                          direction='row'
                          spacing={1}
                          alignItems='center'
                          sx={{ flex: 1 }}
                        >
                          <Typography variant='subtitle2' fontWeight={600}>
                            {group.label}
                          </Typography>
                          <Chip label={group.items.length} size='small' />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <PreviewItems items={group.items} />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Stack>
              )
            )}
          </Box>
        </Stack>
      )}
    </BaseDialog>
  );
};

export default RolloverDataDialog;
